/**
 * VSC report display/filter configs.
 */
import { FileEdit, Send, CheckCircle2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';
import type { VscReportStatus, VscReportFilters } from '@/types/vsc-report.types';

/** Badge + icon config for a VSC report status. */
export interface VscReportStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Allowed rating values (1–5). */
export const VSC_RATING_VALUES = [1, 2, 3, 4, 5] as const;

/** Statuses shown in the VSC report filter. */
export const VSC_REPORT_STATUS_FILTER_KEYS: readonly VscReportStatus[] = [
  'to_fill',
  'submitted',
  'validated',
];

/** Badge config for each VSC report status. */
export const VSC_REPORT_STATUS_CONFIG: Record<VscReportStatus, VscReportStatusConfig> = {
  to_fill: {
    label: 'À saisir',
    icon: FileEdit,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
  },
  submitted: {
    label: 'Soumis',
    icon: Send,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  validated: {
    label: 'Validé',
    icon: CheckCircle2,
    badgeVariant: 'active',
  },
};

/** Default VSC report list filters. */
export const DEFAULT_VSC_REPORT_FILTERS: VscReportFilters = {
  statuses: [],
  dateRange: 'month',
  customDateFrom: undefined,
  customDateTo: undefined,
  search: undefined,
};

/** Filter group IDs for the VSC reports {@link DataTable} configure panel. */
export type FilterGroupId =
  | 'filter-period'
  | 'filter-date-range'
  | 'filter-status'
  | 'filter-establishment'
  | 'filter-search';

/** Configure-panel order for VSC report filter groups. */
export const ALL_VSC_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-period',
  'filter-date-range',
  'filter-status',
  'filter-establishment',
  'filter-search',
];

/** Row-level RBAC flags passed into the VSC report table. */
export type VscReportPageActions = {
  canEditRow?: boolean;
  canDeleteRow?: boolean;
  canViewRow?: boolean;
  canValidate?: boolean;
};

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-period': 'Période',
  'filter-date-range': 'Date personnalisée',
  'filter-status': 'Statut',
  'filter-establishment': 'Établissement',
  'filter-search': 'Recherche nom VSC',
};

/** React Query keys invalidated after a VSC report create / save / validate. */
export { DATE_RANGE_PERIOD_OPTIONS_FULL } from '@/constants/shared-catalog';

/** React Query keys invalidated after a VSC report create / save / validate. */
export const VSC_REPORT_FORM_QUERY_KEYS = {
  list: ['vsc-reports'] as const,
  onePrefix: ['vsc-report'] as const,
  one: (id: string) => ['vsc-report', id] as const,
};
